import { mailWorkspaceSchema, mailWorkflowStateSchema, mailWorkStatusSchema, } from './mail-workflow.js';
import { z } from 'zod';
import { mailAddressSchema } from './mail-account.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { paginationMetaSchema, paginationQuerySchema } from './pagination.js';
import { MAIL_ATTACHMENT_BLOCK_REASONS, MAIL_MAX_PARTICIPANTS, MAIL_SOURCE_FINDINGS, MAIL_MAX_SUBJECT_CHARACTERS, } from '../constants/mail.js';
export const mailParticipantSchema = z
    .object({
    address: mailAddressSchema.transform((value) => value.toLowerCase()),
    name: z.string().max(320),
})
    .strict();
export const mailParticipantsSchema = z.array(mailParticipantSchema).max(MAIL_MAX_PARTICIPANTS);
export const mailMessageHeaderIdSchema = z
    .string()
    .max(MAIL_MAX_SUBJECT_CHARACTERS)
    .regex(/^<[^<>\s@]+@[^<>\s@]+>$/u);
export const mailBccEvidenceSchema = z
    .object({
    addresses: mailParticipantsSchema,
    knowledge: z.enum(['UNKNOWN', 'OBSERVED', 'COMPLETE']),
    provenance: z.enum(['UNKNOWN', 'RAW_HEADER', 'PROVIDER', 'ERP_ENVELOPE']),
    addressErrors: z.boolean().optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.knowledge === 'UNKNOWN' &&
        (value.addresses.length > 0 || value.provenance !== 'UNKNOWN')) ||
        (value.knowledge !== 'UNKNOWN' && value.provenance === 'UNKNOWN') ||
        (value.knowledge === 'COMPLETE' && value.provenance === 'RAW_HEADER'))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'mail.validation.bccEvidence' });
});
export const mailSourceFindingSchema = z.enum(MAIL_SOURCE_FINDINGS);
export const mailAttachmentBlockReasonSchema = z.enum(MAIL_ATTACHMENT_BLOCK_REASONS);
export const mailFolderRoleSchema = z.enum([
    'INBOX',
    'SENT',
    'ARCHIVE',
    'DRAFTS',
    'JUNK',
    'TRASH',
    'ALL',
    'CUSTOM',
    'UNKNOWN',
]);
export const mailDirectionSchema = z.enum(['INCOMING', 'OUTGOING', 'UNKNOWN']);
export const mailPartSchema = z
    .object({
    id: idSchema,
    fileName: z.string(),
    mimeType: z.string(),
    fileSize: z.number().int().nonnegative(),
    disposition: z.enum(['ATTACHMENT', 'INLINE']),
    blockedReason: mailAttachmentBlockReasonSchema.nullable(),
    downloadAllowed: z.boolean(),
})
    .strict();
export const mailLocationSchema = z
    .object({
    id: idSchema,
    folderId: idSchema,
    folderPath: z.string(),
    folderRole: mailFolderRoleSchema,
    roleEvidence: z.enum(['SPECIAL_USE', 'PROVIDER', 'CONFIGURED', 'UNKNOWN']),
    uidValidity: z.string(),
    uid: z.string(),
    flags: z.array(z.string()),
    direction: mailDirectionSchema,
    isDraft: z.boolean(),
    isSpam: z.boolean(),
    internalDate: isoDateTimeSchema.nullable(),
    missingAt: isoDateTimeSchema.nullable(),
})
    .strict();
export const mailSourceSchema = z
    .object({
    id: idSchema,
    disclosure: z.enum(['MAILBOX', 'RECORD']).default('MAILBOX'),
    canRematch: z.boolean().default(false),
    direction: mailDirectionSchema.default('UNKNOWN'),
    isSpam: z.boolean().default(false),
    isDraft: z.boolean().default(false),
    accountId: idSchema.nullable(),
    accountName: z.string(),
    primaryAddress: mailAddressSchema.nullable(),
    connectionGeneration: z.number().int().positive().nullable(),
    historical: z.boolean(),
    firstObservedAt: isoDateTimeSchema,
    findings: z.array(mailSourceFindingSchema),
    locations: z.array(mailLocationSchema),
    parts: z.array(mailPartSchema),
    originalDownloadAllowed: z.boolean(),
    confidential: mailBccEvidenceSchema.optional(),
    recordVisibility: z.enum(['PRIVATE', 'BUSINESS', 'EXCLUDED']),
    businessIrrelevant: z.boolean(),
})
    .strict();
export const mailMessageSummarySchema = z
    .object({
    timezone: z.string().min(1),
    id: idSchema,
    subject: z.string(),
    from: mailParticipantsSchema,
    to: mailParticipantsSchema,
    cc: mailParticipantsSchema,
    sentAt: isoDateTimeSchema.nullable(),
    firstImportedAt: isoDateTimeSchema,
    sources: z.array(mailSourceSchema),
    read: z.boolean(),
    processing: mailWorkflowStateSchema.optional(),
})
    .strict();
export const mailMessageDetailSchema = mailMessageSummarySchema
    .extend({ bodyText: z.string() })
    .strict();
export const mailMessageListQuerySchema = paginationQuerySchema.omit({ sort: true }).extend({
    sort: z.enum(['sentAt:asc', 'sentAt:desc', 'subject:asc', 'subject:desc']).default('sentAt:desc'),
    accountId: idSchema.optional(),
    folderId: idSchema.optional(),
    workspace: mailWorkspaceSchema.optional(),
    direction: mailDirectionSchema.optional(),
    read: z.enum(['READ', 'UNREAD']).optional(),
    role: z.enum(['CUSTOMER', 'PROSPECT', 'SUPPLIER']).optional(),
    partnerId: idSchema.optional(),
    association: z.enum(['LINKED', 'UNASSIGNED', 'AMBIGUOUS']).optional(),
    workStatus: mailWorkStatusSchema.optional(),
    assignedToId: idSchema.optional(),
    unowned: z.enum(['YES']).optional(),
    spam: z.enum(['ALL', 'ONLY', 'EXCLUDE']).optional(),
    attachments: z.enum(['ALL', 'YES', 'NO']).optional(),
    dateFrom: z.string().date().optional(),
    dateTo: z.string().date().optional(),
});
export const mailMessageReadQuerySchema = z.object({ sourceId: idSchema.optional() }).strict();
export const mailMessageListResponseSchema = z
    .object({
    data: z.array(mailMessageSummarySchema),
    meta: paginationMetaSchema,
})
    .strict();
export const mailThreadSchema = z
    .object({ data: z.array(mailMessageSummarySchema), truncated: z.boolean() })
    .strict();
//# sourceMappingURL=mail-message.js.map